<script setup lang="tsx">
import type { FormInstance } from 'element-plus';

import type { VxeGridProps } from '#/adapter/vxe-table';

import { computed, ref, useTemplateRef } from 'vue';

import { useVbenModal } from '@vben/common-ui';

import {
  ElButton,
  ElDivider,
  ElForm,
  ElFormItem,
  ElImage,
  ElInput,
  ElLink,
  ElMessage,
  ElMessageBox,
  ElTag,
} from 'element-plus';

import { useVbenVxeGrid } from '#/adapter/vxe-table';
import {
  addBuyerApi,
  buyerListApi,
  updateBuyerApi,
  updateBuyerStatusApi,
} from '#/api/core/markey';

const loading = ref(false);
const pluginDownloadUrl =
  'https://fq-repository.fengqian.cc/tkct/plugin_download/b_auto_order/latest.zip';

const formSchema = computed(() => [
  {
    component: 'Input',
    componentProps: {
      placeholder: '请输入买家号',
    },
    fieldName: 'codes',
    label: '买家号',
  },
  {
    component: 'Select',
    componentProps: {
      placeholder: '请选择状态',
      options: [],
      clearable: true,
    },
    fieldName: 'types',
    label: '状态',
  },
]);

const gridOptions: VxeGridProps<Record<string, any>> = {
  autoResize: true,
  height: '100%',
  columns: [
    { title: '序号', type: 'seq', width: 50 },
    {
      title: '买家号',
      field: 'buyerName',
      showOverflow: false,
      minWidth: 230,
    },
    {
      title: '状态',
      field: 'accountStatusName',
      showOverflow: false,
      minWidth: 100,
      slots: { default: 'accountStatusName' },
    },
    {
      title: '备注',
      field: 'remark',
      showOverflow: false,
      minWidth: 230,
    },
    {
      title: '操作时间',
      field: 'operatingTime',
      showOverflow: false,
      minWidth: 230,
    },
    {
      title: '操作',
      field: 'action',
      showOverflow: false,
      width: 120,
      slots: { default: 'action' },
    },
  ],
  data: [],
  pagerConfig: {
    pageSize: 200,
    pageSizes: [200],
  },
  toolbarConfig: {
    // 是否显示搜索表单控制按钮
    // @ts-ignore 正式环境时有完整的类型声明
    search: true,
  },
  proxyConfig: {
    ajax: {
      query: async ({ page }, formValues) => {
        const { rows, total } = await buyerListApi({
          pageNum: page.currentPage,
          pageSize: page.pageSize,
          ...formValues,
        });
        return {
          total,
          items: rows,
        };
      },
    },
  },
};

const [Grid, gridApi] = useVbenVxeGrid({
  formOptions: {
    // 默认展开
    collapsed: false,
    schema: formSchema.value,
    // 控制表单是否显示折叠按钮
    showCollapseButton: true,
    submitButtonOptions: {
      content: '查询',
    },
    // 是否在字段值改变时提交表单
    submitOnChange: false,
    // 按下回车时是否提交表单
    submitOnEnter: true,
  },
  gridOptions,
});

// 新增买家号
const formEdit = ref<Record<string, any>>({
  buyerName: '',
  remark: '',
});
const formEditRules = ref({
  buyerName: [{ required: true, message: '请输入买家号', trigger: 'blur' }],
});
const formEditRef = useTemplateRef<FormInstance>('formEditRef');
const [Modal, modalApi] = useVbenModal({
  onConfirm: async () => {
    const validateRes = await formEditRef.value?.validate();

    if (validateRes) {
      loading.value = true;
      const api = formEdit.value.id ? updateBuyerApi : addBuyerApi;
      api(formEdit.value)
        .then(() => {
          modalApi.close();
          gridApi.reload();
          ElMessage.success(`${formEdit.value.id ? '更新' : '新增'}买家号成功`);
        })
        .finally(() => {
          loading.value = false;
        });
    }
  },
});

function handleChangeStatus(row: Record<string, any>) {
  ElMessageBox.confirm(
    `确定${row.accountStatus === 1 ? '禁用' : '启用'}买家号${row.buyerName}吗？`,
    {
      confirmButtonText: '确定',
      cancelButtonText: '取消',
      title: '提示',
    },
  ).then(() => {
    loading.value = true;
    updateBuyerStatusApi({
      id: row.id,
      accountStatus: row.accountStatus === 1 ? 0 : 1,
    })
      .then(() => {
        gridApi.query();
        ElMessage.success('操作成功');
      })
      .finally(() => {
        loading.value = false;
      });
  });
}

// 编辑买家号

function handleEdit(row: Record<string, any>) {
  formEdit.value = {
    ...row,
  };
  modalApi.open();
}

function downloadPlugin() {
  const a = document.createElement('a');
  a.href = `${pluginDownloadUrl}?d=${Date.now()}`;
  a.download = '速蜂拍单插件.zip';
  document.body.append(a);
  a.click();
  a.remove();
}
</script>
<template>
  <div class="h-full w-full bg-white">
    <div class="flex flex-col items-center justify-center bg-white p-4 pb-0">
      <div class="flex flex-none items-center">
        <i class="icon-[mdi--check-circle] text-2xl text-blue-500"></i>
        尊敬的商家：你已成功开通自动拍单工具使用通道，可先下载以下工具插件包，根据安装步骤/教程进行使用。
      </div>
      <div class="mt-2 flex justify-center">
        <div class="mx-4 flex flex-col items-center">
          <ElImage
            src="/static/toolZip.png"
            alt="自动拍单工具插件包"
            class="h-[60px] w-[60px]"
          />
          <ElButton
            class="mt-2"
            type="primary"
            size="small"
            @click="downloadPlugin"
          >
            下载
          </ElButton>
        </div>
      </div>
    </div>
    <ElDivider class="bg-white" style="margin: 24px 0 0" />
    <div style="height: calc(100% - 186px); overflow: hidden">
      <Grid>
        <template #toolbar-actions>
          <ElButton type="primary" @click="handleEdit({})"> 新 增 </ElButton>
        </template>
        <template #accountStatusName="{ row }">
          <div class="flex justify-center">
            <ElTag
              :type="row.accountStatus === 1 ? 'primary' : 'danger'"
              size="small"
              class="w-fit"
            >
              {{ row.accountStatusName }}
            </ElTag>
          </div>
        </template>
        <template #action="{ row }">
          <div class="flex flex-col">
            <ElLink
              type="primary"
              size="small"
              :underline="false"
              @click="handleEdit(row)"
            >
              编辑
            </ElLink>
            <ElLink
              type="primary"
              size="small"
              :underline="false"
              @click="handleChangeStatus(row)"
            >
              {{ row.accountStatus === 1 ? '禁用' : '启用' }}
            </ElLink>
          </div>
        </template>
      </Grid>
    </div>

    <Modal title="新增买家号" :loading="loading">
      <ElForm
        :model="formEdit"
        label-width="80px"
        class="pr-2"
        ref="formEditRef"
        :rules="formEditRules"
      >
        <ElFormItem label="买家号" prop="buyerName">
          <ElInput v-model="formEdit.buyerName" placeholder="请输入买家号" />
        </ElFormItem>
        <ElFormItem label="备注" prop="remark">
          <ElInput
            v-model="formEdit.remark"
            placeholder="请输入备注"
            type="textarea"
            :rows="8"
          />
        </ElFormItem>
      </ElForm>
    </Modal>
  </div>
</template>
